import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Video, Phone, PhoneOff, Mic, MicOff, Camera, CameraOff,
  Monitor, MonitorOff, MessageCircle, Users, Send, X, Clock,
  Plus, Play, StopCircle, UserCheck, UserX, Shield, Lock, Mail
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDistanceToNow, format } from "date-fns";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";

// Meeting List Page
function MeetingList() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [meetingImage, setMeetingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const fileRef = useRef(null);
  const isAdmin = user?.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin";

  // Email-invite state (shown after a meeting exists)
  const [emailInviteFor, setEmailInviteFor] = useState(null); // {meeting_id, title}
  const [inviteEmails, setInviteEmails] = useState("");
  const [sendingInvites, setSendingInvites] = useState(false);

  const sendEmailInvites = async () => {
    if (!emailInviteFor || !inviteEmails.trim()) { toast.error("Enter at least one email"); return; }
    setSendingInvites(true);
    try {
      const res = await axios.post(
        `${API}/meetings/${emailInviteFor.meeting_id}/invite-emails`,
        { emails: inviteEmails, origin: window.location.origin },
        { withCredentials: true }
      );
      if (!res.data.configured) {
        toast.error("SendGrid is not configured. Add an API key under Admin → Settings.");
      } else if (res.data.sent_count > 0) {
        toast.success(`Sent ${res.data.sent_count} invite(s)${res.data.failed_count ? `, ${res.data.failed_count} failed` : ""}`);
      } else {
        toast.error("All invites failed to send");
      }
      setEmailInviteFor(null);
      setInviteEmails("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invites");
    } finally { setSendingInvites(false); }
  };

  useEffect(() => { fetchMeetings(); }, []);

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(`${API}/meetings`, { withCredentials: true });
      setMeetings(res.data || []);
    } catch { console.error("Failed to fetch meetings"); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/list`, { withCredentials: true });
      setAllUsers(res.data || []);
    } catch { console.error("Failed to fetch users"); }
  };

  const handleOpenCreate = () => {
    setCreateOpen(true);
    fetchUsers();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMeetingImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    if (!title.trim()) { toast.error("Enter a meeting title"); return; }
    setCreating(true);
    const fd = new FormData();
    fd.append("title", title);
    fd.append("description", description);
    fd.append("invited_users", selectedUsers.map(u => u.user_id).join(","));
    if (meetingImage) fd.append("file", meetingImage);
    try {
      await axios.post(`${API}/meetings`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Meeting created!");
      setCreateOpen(false);
      setTitle(""); setDescription(""); setMeetingImage(null); setImagePreview(null); setSelectedUsers([]);
      fetchMeetings();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create meeting"); }
    finally { setCreating(false); }
  };

  const toggleUser = (u) => {
    setSelectedUsers(prev => prev.find(s => s.user_id === u.user_id) ? prev.filter(s => s.user_id !== u.user_id) : [...prev, u]);
  };

  const filteredUsers = allUsers.filter(u => u.user_id !== user?.user_id && u.name?.toLowerCase().includes(userSearch.toLowerCase()));

  const liveMeetings = meetings.filter(m => m.status === "live");
  const scheduledMeetings = meetings.filter(m => m.status === "scheduled");
  const pastMeetings = meetings.filter(m => m.status === "ended");

  return (
    <Layout>
      <section className="gradient-hero py-12">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Conference</span>
              <h1 className="text-heading-1 text-white mt-3 mb-3">Conference Room</h1>
              <p className="text-white/80 text-lg">Join meetings, collaborate, and connect with the team.</p>
            </div>
            {isAdmin && (
              <Button onClick={handleOpenCreate} className="bg-awareness-blue text-white hover:bg-awareness-blue/90" data-testid="create-meeting-btn">
                <Plus className="w-4 h-4 mr-2" /> New Meeting
              </Button>
            )}
          </div>
        </div>
      </section>

      <section className="section-spacing">
        <div className="container-custom space-y-10">
          {/* Live Now */}
          {liveMeetings.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-deep-blue mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" /> Live Now
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {liveMeetings.map(m => (
                  <div key={m.meeting_id} className="bg-white rounded-xl border-2 border-red-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`meeting-${m.meeting_id}`}>
                    <Link to={`/conference/${m.meeting_id}`}>
                      <div className="aspect-video bg-gradient-to-r from-deep-blue to-slate-800 flex items-center justify-center relative overflow-hidden">
                        {m.image_url ? <img src={`${API.replace('/api', '')}${m.image_url}`} alt="" className="w-full h-full object-cover" /> : <Video className="w-10 h-10 text-awareness-blue" />}
                        <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1"><div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE</span>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-deep-blue">{m.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">Host: {m.host_name}</p>
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {m.participants?.length || 0} participants</p>
                      </div>
                    </Link>
                    {isAdmin && (
                      <div className="px-4 pb-4">
                        <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => setEmailInviteFor({ meeting_id: m.meeting_id, title: m.title })} data-testid={`invite-email-${m.meeting_id}`}>
                          <Mail className="w-3 h-3 mr-1" /> Invite by Email
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scheduled */}
          {scheduledMeetings.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-deep-blue mb-4 flex items-center gap-2"><Clock className="w-5 h-5" /> Scheduled</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {scheduledMeetings.map(m => (
                  <div key={m.meeting_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`meeting-${m.meeting_id}`}>
                    <Link to={`/conference/${m.meeting_id}`}>
                      <div className="aspect-video bg-slate-50 flex items-center justify-center relative overflow-hidden">
                        {m.image_url ? <img src={`${API.replace('/api', '')}${m.image_url}`} alt="" className="w-full h-full object-cover" /> : <Video className="w-10 h-10 text-slate-400" />}
                        <span className="absolute top-3 left-3 bg-awareness-blue/10 text-awareness-blue text-xs font-bold px-2 py-1 rounded">SCHEDULED</span>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-deep-blue">{m.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">Host: {m.host_name}</p>
                        {m.scheduled_at && <p className="text-xs text-slate-400 mt-1">{format(new Date(m.scheduled_at), "MMM d, yyyy h:mm a")}</p>}
                        {m.invited_users?.length > 0 && <p className="text-xs text-awareness-blue mt-1">{m.invited_users.length} invited</p>}
                      </div>
                    </Link>
                    {isAdmin && (
                      <div className="px-4 pb-4">
                        <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => setEmailInviteFor({ meeting_id: m.meeting_id, title: m.title })} data-testid={`invite-email-${m.meeting_id}`}>
                          <Mail className="w-3 h-3 mr-1" /> Invite by Email
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Past Meetings */}
          {pastMeetings.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-deep-blue mb-4">Past Meetings</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pastMeetings.slice(0, 6).map(m => (
                  <div key={m.meeting_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`past-meeting-${m.meeting_id}`}>
                    <h3 className="font-semibold text-deep-blue">{m.title}</h3>
                    <p className="text-sm text-slate-500">Host: {m.host_name}</p>
                    <p className="text-xs text-slate-400 mt-1">{m.ended_at ? formatDistanceToNow(new Date(m.ended_at), { addSuffix: true }) : ""}</p>
                    {m.has_recording && (
                      <a href={`${API}/meetings/${m.meeting_id}/recording`} className="text-xs text-awareness-blue mt-2 inline-flex items-center gap-1 hover:underline"><Play className="w-3 h-3" /> Watch Recording</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {meetings.length === 0 && (
            <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
              <Video className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600">No meetings yet</h3>
              <p className="text-slate-400 mt-1">{isAdmin ? "Create your first meeting!" : "Check back for upcoming meetings."}</p>
            </div>
          )}
        </div>
      </section>

      {/* Email Invite Dialog */}
      <Dialog open={!!emailInviteFor} onOpenChange={(o) => { if (!o) { setEmailInviteFor(null); setInviteEmails(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="email-invite-dialog">
          <DialogHeader><DialogTitle>Invite by Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Send a join link to one or more email addresses for <strong>{emailInviteFor?.title}</strong>.</p>
            <Textarea
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              placeholder="alice@example.com, bob@example.com&#10;carol@example.com"
              rows={5}
              data-testid="invite-emails-textarea"
            />
            <p className="text-xs text-slate-400">Separate addresses with commas or new lines. Recipients will receive a one-click link to join the meeting.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setEmailInviteFor(null); setInviteEmails(""); }}>Cancel</Button>
              <Button onClick={sendEmailInvites} disabled={sendingInvites || !inviteEmails.trim()} className="bg-deep-blue text-white" data-testid="send-invites-btn">
                <Send className="w-4 h-4 mr-2" /> {sendingInvites ? "Sending..." : "Send Invites"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Meeting Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-meeting-dialog">
          <DialogHeader><DialogTitle>New Meeting</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Team Standup" data-testid="meeting-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's this meeting about?" rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium">Cover Image</label>
              <div className="mt-1">
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-deep-blue/10 file:text-deep-blue hover:file:bg-deep-blue/20" data-testid="meeting-image-input" />
                {imagePreview && (
                  <div className="relative mt-2">
                    <img src={imagePreview} alt="Preview" className="h-32 rounded-lg object-cover" />
                    <button onClick={() => { setMeetingImage(null); setImagePreview(null); if (fileRef.current) fileRef.current.value = ""; }} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Invite Members</label>
              <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search members..." className="mt-1 mb-2" data-testid="meeting-search-users" />
              {/* Selected users */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedUsers.map(u => (
                    <span key={u.user_id} className="inline-flex items-center gap-1 bg-deep-blue/10 text-deep-blue text-xs px-2 py-1 rounded-full font-medium">
                      {u.name}
                      <button onClick={() => toggleUser(u)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              {/* User list */}
              <div className="max-h-36 overflow-y-auto border rounded-lg divide-y">
                {filteredUsers.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-3">{allUsers.length <= 1 ? "No other members registered yet" : "No matching members"}</p>
                ) : (
                  filteredUsers.map(u => (
                    <button key={u.user_id} onClick={() => toggleUser(u)} className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 ${selectedUsers.find(s => s.user_id === u.user_id) ? 'bg-deep-blue/5' : ''}`} data-testid={`invite-user-${u.user_id}`}>
                      <div className="w-7 h-7 rounded-full bg-deep-blue flex items-center justify-center text-white text-xs font-bold shrink-0">{u.name?.charAt(0)?.toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-deep-blue">{u.name}</span>
                        <span className="text-xs text-slate-400 ml-2">{u.email}</span>
                      </div>
                      {selectedUsers.find(s => s.user_id === u.user_id) && <UserCheck className="w-4 h-4 text-green-500 shrink-0" />}
                    </button>
                  ))
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-deep-blue text-white" data-testid="create-meeting-submit">{creating ? "Creating..." : "Create Meeting"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// Meeting Room
function MeetingRoom({ meetingId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [status, setStatus] = useState("loading");
  const [participants, setParticipants] = useState([]);
  const [waitingRoom, setWaitingRoom] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef({});
  const wsRef = useRef(null);
  const peerConnections = useRef({});
  const localStreamRef = useRef(null);
  const chatEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunks = useRef([]);

  const isHost = user?.user_id === meeting?.host_id;
  const isAdmin = user?.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin";

  // Callback ref for conference local video
  const localVideoCallback = useCallback((node) => {
    localVideoRef.current = node;
    if (node && localStreamRef.current) {
      node.srcObject = localStreamRef.current;
      node.play().catch(() => {});
    }
  }, [status]);

  useEffect(() => { fetchMeeting(); return cleanup; }, [meetingId]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  const cleanup = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
    Object.values(peerConnections.current).forEach(pc => pc.close());
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
  };

  const fetchMeeting = async () => {
    try {
      const res = await axios.get(`${API}/meetings/${meetingId}`, { withCredentials: true });
      setMeeting(res.data);
      setWaitingRoom(res.data.waiting_room || []);
      if (res.data.status === "ended") { setStatus("ended"); return; }
      if (res.data.status === "scheduled" && (res.data.host_id === user?.user_id || isAdmin)) {
        setStatus("host_start");
      } else if (res.data.status === "scheduled") {
        setStatus("scheduled");
      } else {
        requestJoin();
      }
    } catch { toast.error("Meeting not found"); navigate("/conference"); }
  };

  const startMeeting = async () => {
    try {
      await axios.post(`${API}/meetings/${meetingId}/start`, {}, { withCredentials: true });
      setStatus("joining");
      await joinMeeting();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to start"); }
  };

  const requestJoin = async () => {
    try {
      const res = await axios.post(`${API}/meetings/${meetingId}/join`, {}, { withCredentials: true });
      if (res.data.status === "admitted") {
        setStatus("joining");
        await joinMeeting();
      } else {
        setStatus("waiting");
        // Connect WebSocket while waiting so we receive the admit message
        connectWaitingWebSocket();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot join"); }
  };

  const connectWaitingWebSocket = () => {
    const wsUrl = API.replace("/api", "").replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/meeting/${meetingId}`);
    wsRef.current = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "identify", user_id: user.user_id, user_name: user.name }));
    };
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "user_admitted" && data.user_id === user.user_id) {
        toast.success("You've been admitted!");
        ws.close();
        joinMeeting();
      } else if (data.type === "user_rejected" && data.user_id === user.user_id) {
        toast.error("Your request was declined");
        setStatus("rejected");
        ws.close();
      }
    };
  };

  const joinMeeting = async () => {
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }, 
          audio: true 
        });
      } catch (camErr) {
        // Fallback: try audio only
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
          toast.info("Joined with audio only — camera unavailable");
        } catch {
          toast.error("Could not access microphone. Check browser permissions.");
          setStatus("error");
          return;
        }
      }
      localStreamRef.current = stream;

      // Start recording
      try {
        const mr = new MediaRecorder(stream, { mimeType: "video/webm" });
        mr.ondataavailable = (e) => { if (e.data.size > 0) recordedChunks.current.push(e.data); };
        mr.start(1000);
        mediaRecorderRef.current = mr;
      } catch {}

      connectWebSocket();
      setStatus("in_meeting");
    } catch (err) {
      toast.error("Failed to join meeting");
      setStatus("error");
    }
  };

  const connectWebSocket = () => {
    const wsUrl = API.replace('/api', '').replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/meeting/${meetingId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "identify", user_id: user.user_id, user_name: user.name }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "chat") setChatMessages(prev => [...prev, data]);
      else if (data.type === "participants_update") {
        setParticipants(data.participants || []);
        // Only create offers for peers with LOWER user_id (avoids both sides offering simultaneously)
        for (const p of (data.participants || [])) {
          if (p.user_id !== user.user_id && !peerConnections.current[p.user_id]) {
            if (user.user_id > p.user_id) {
              createOfferForPeer(p.user_id);
            }
          }
        }
      }
      else if (data.type === "meeting_ended") { toast.info("Meeting ended"); setStatus("ended"); cleanup(); }
      else if (data.type === "waiting_room_update") setWaitingRoom(prev => [...prev, data.user]);
      else if (data.type === "user_admitted" && data.user_id === user.user_id) joinMeeting();
      else if (data.type === "user_rejected" && data.user_id === user.user_id) { toast.error("Your request was declined"); setStatus("rejected"); }
      else if (data.type === "user_left") {
        toast.info(`${data.user_name} left`);
        if (peerConnections.current[data.user_id]) {
          peerConnections.current[data.user_id].close();
          delete peerConnections.current[data.user_id];
        }
      }
      else if (data.type === "offer") handleOffer(data);
      else if (data.type === "answer") handleAnswer(data);
      else if (data.type === "ice_candidate") handleIce(data);
    };
  };

  const createOfferForPeer = async (peerId) => {
    const pc = createPeerConnection(peerId);
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({ type: "offer", sdp: offer, target_id: peerId }));
    } catch (e) { console.warn("Failed to create offer for", peerId, e); }
  };

  const handleOffer = async (data) => {
    const pc = createPeerConnection(data.from_id);
    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    wsRef.current?.send(JSON.stringify({ type: "answer", sdp: answer, target_id: data.from_id }));
  };

  const handleAnswer = async (data) => {
    const pc = peerConnections.current[data.from_id];
    if (pc) await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
  };

  const handleIce = async (data) => {
    const pc = peerConnections.current[data.from_id];
    if (pc && data.candidate) await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
  };

  const createPeerConnection = (peerId) => {
    // Close existing connection if any
    if (peerConnections.current[peerId]) {
      peerConnections.current[peerId].close();
    }
    const pc = new RTCPeerConnection({ iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "turn:standard.relay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
      { urls: "turn:standard.relay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
      { urls: "turn:standard.relay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
    ] });
    peerConnections.current[peerId] = pc;
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
    pc.ontrack = (event) => {
      // Retry attaching to video element (it may not be rendered yet)
      const attach = () => {
        const el = document.getElementById(`remote-video-${peerId}`);
        if (el) { el.srcObject = event.streams[0]; el.play().catch(() => {}); return true; }
        return false;
      };
      if (!attach()) {
        let attempts = 0;
        const interval = setInterval(() => {
          if (attach() || ++attempts > 20) clearInterval(interval);
        }, 200);
      }
    };
    pc.onicecandidate = (event) => {
      if (event.candidate) wsRef.current?.send(JSON.stringify({ type: "ice_candidate", candidate: event.candidate, target_id: peerId }));
    };
    return pc;
  };

  const endMeeting = async () => {
    // Wait for recorder to finish
    await new Promise((resolve) => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.onstop = () => resolve();
        mediaRecorderRef.current.stop();
      } else resolve();
    });
    try {
      await axios.post(`${API}/meetings/${meetingId}/end`, {}, { withCredentials: true });
      if (recordedChunks.current.length > 0) {
        toast.info("Saving recording...");
        const blob = new Blob(recordedChunks.current, { type: "video/webm" });
        const fd = new FormData();
        fd.append("file", blob, "recording.webm");
        await axios.post(`${API}/meetings/${meetingId}/recording`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }).catch(() => {});
      }
      toast.success("Meeting ended & saved");
      cleanup();
      navigate("/conference");
    } catch { toast.error("Failed to end meeting"); }
  };

  const leaveMeeting = () => { cleanup(); navigate("/conference"); };

  const admitUser = async (userId) => {
    try {
      await axios.post(`${API}/meetings/${meetingId}/admit`, { user_id: userId }, { withCredentials: true });
      setWaitingRoom(prev => prev.filter(w => w.user_id !== userId));
      toast.success("User admitted");
    } catch { toast.error("Failed to admit"); }
  };

  const rejectUser = async (userId) => {
    try {
      await axios.post(`${API}/meetings/${meetingId}/reject`, { user_id: userId }, { withCredentials: true });
      setWaitingRoom(prev => prev.filter(w => w.user_id !== userId));
    } catch { toast.error("Failed to reject"); }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setAudioEnabled(prev => !prev);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setVideoEnabled(prev => !prev);
    }
  };

  const sendChat = () => {
    if (!chatInput.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "chat", user_name: user.name, content: chatInput }));
    setChatInput("");
  };

  // Pre-meeting states
  if (status === "loading") return <Layout><div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-blue" /></div></Layout>;

  if (status === "scheduled") return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center shadow-lg">
          <Clock className="w-16 h-16 text-awareness-blue mx-auto mb-4" />
          <h2 className="text-xl font-bold text-deep-blue mb-2">{meeting?.title}</h2>
          <p className="text-slate-500 mb-4">This meeting hasn't started yet. Please wait for the host.</p>
          <Link to="/conference"><Button variant="outline">Back to Meetings</Button></Link>
        </div>
      </div>
    </Layout>
  );

  if (status === "host_start") return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center shadow-lg">
          <Video className="w-16 h-16 text-deep-blue mx-auto mb-4" />
          <h2 className="text-xl font-bold text-deep-blue mb-2">{meeting?.title}</h2>
          <p className="text-slate-500 mb-6">You're the host. Start when ready.</p>
          <Button onClick={startMeeting} className="bg-green-600 text-white hover:bg-green-700" data-testid="start-meeting-btn"><Play className="w-4 h-4 mr-2" /> Start Meeting</Button>
        </div>
      </div>
    </Layout>
  );

  if (status === "waiting") return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center shadow-lg">
          <Lock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-deep-blue mb-2">Waiting Room</h2>
          <p className="text-slate-500 mb-4">The host will let you in shortly...</p>
          <div className="animate-pulse flex justify-center gap-1 mb-6">{[0,1,2].map(i => <div key={i} className="w-2 h-2 bg-awareness-blue rounded-full" />)}</div>
          <Link to="/conference"><Button variant="outline" size="sm">Leave</Button></Link>
        </div>
      </div>
    </Layout>
  );

  if (status === "ended") return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center shadow-lg">
          <PhoneOff className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-deep-blue mb-2">Meeting Ended</h2>
          <p className="text-slate-500 mb-6">{meeting?.title}</p>
          {meeting?.has_recording && (
            <a href={`${API}/meetings/${meetingId}/recording`} className="text-awareness-blue hover:underline text-sm flex items-center justify-center gap-1 mb-4"><Play className="w-4 h-4" /> Watch Recording</a>
          )}
          <Link to="/conference"><Button variant="outline">Back to Meetings</Button></Link>
        </div>
      </div>
    </Layout>
  );

  // In-meeting view
  return (
    <div className="h-screen bg-slate-900 flex flex-col" data-testid="meeting-room">
      {/* Header */}
      <div className="bg-slate-800 px-4 py-2 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-awareness-blue" />
          <span className="text-white font-semibold text-sm">{meeting?.title}</span>
          <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded animate-pulse">LIVE</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-xs flex items-center gap-1"><Users className="w-3 h-3" /> {participants.length}</span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video grid */}
        <div className="flex-1 p-4">
          <div className={`grid gap-3 h-full ${participants.length <= 1 ? 'grid-cols-1' : participants.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {/* Local video */}
            <div className="relative bg-slate-800 rounded-xl overflow-hidden">
              <video ref={localVideoCallback} autoPlay muted playsInline className={`w-full h-full object-cover ${!videoEnabled ? 'hidden' : ''}`} style={{ transform: 'scaleX(-1)' }} />
              {!videoEnabled && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-slate-700 to-slate-800">
                  {user?.picture ? (
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-awareness-blue/30">
                      <img src={user.picture.startsWith('/api') ? `${API.replace('/api', '')}${user.picture}` : user.picture} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <>
                      <img src={LOGO_URL} alt="Blue vs Blue" className="h-16 w-auto rounded-lg mb-3" />
                      <div className="flex items-center gap-2 mt-1">
                        <CameraOff className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-400 text-xs">Camera off</span>
                      </div>
                    </>
                  )}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-400 text-[10px] font-bold uppercase">Live</span>
                  </div>
                </div>
              )}
              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">{user?.name} (You)</div>
              <img src={LOGO_URL} alt="Blue vs Blue" className="absolute bottom-3 right-3 h-12 w-auto opacity-80 rounded" />
            </div>
            {/* Remote videos */}
            {participants.filter(p => p.user_id !== user?.user_id).map(p => (
              <div key={p.user_id} className="relative bg-slate-800 rounded-xl overflow-hidden flex items-center justify-center">
                <video id={`remote-video-${p.user_id}`} autoPlay playsInline className="w-full h-full object-cover" style={{ zIndex: 1 }} />
                {/* Placeholder behind video - logo + live indicator + camera */}
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-slate-700 to-slate-800">
                  <img src={LOGO_URL} alt="Blue vs Blue" className="h-16 w-auto rounded-lg mb-3" />
                  <div className="flex items-center gap-2">
                    <Camera className="w-4 h-4 text-awareness-blue" />
                    <span className="text-slate-400 text-xs">Connecting...</span>
                  </div>
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-red-400 text-[10px] font-bold uppercase">Live</span>
                  </div>
                </div>
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded" style={{ zIndex: 2 }}>{p.user_name}</div>
                <img src={LOGO_URL} alt="Blue vs Blue" className="absolute bottom-3 right-3 h-12 w-auto opacity-80 rounded" style={{ zIndex: 2 }} />
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar - Chat or Participants */}
        {(showChat || showParticipants) && (
          <div className="w-[320px] bg-slate-800 border-l border-slate-700 flex flex-col">
            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
              <div className="flex gap-2">
                <button onClick={() => { setShowChat(true); setShowParticipants(false); }} className={`text-xs font-semibold px-3 py-1 rounded ${showChat ? 'bg-awareness-blue text-white' : 'text-slate-400'}`}>Chat</button>
                <button onClick={() => { setShowParticipants(true); setShowChat(false); }} className={`text-xs font-semibold px-3 py-1 rounded ${showParticipants ? 'bg-awareness-blue text-white' : 'text-slate-400'}`}>
                  People {waitingRoom.length > 0 && <span className="ml-1 bg-amber-500 text-white text-[10px] px-1 rounded">{waitingRoom.length}</span>}
                </button>
              </div>
              <button onClick={() => { setShowChat(false); setShowParticipants(false); }} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>

            {showChat && (
              <>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className="text-sm"><span className="font-semibold text-awareness-blue">{msg.user_name}: </span><span className="text-slate-300">{msg.content}</span></div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-3 border-t border-slate-700 flex gap-2">
                  <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Message..." className="bg-slate-700 border-slate-600 text-white text-sm placeholder:text-slate-400" data-testid="meeting-chat-input" />
                  <Button size="sm" onClick={sendChat} className="bg-awareness-blue"><Send className="w-4 h-4" /></Button>
                </div>
              </>
            )}

            {showParticipants && (
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {/* Waiting Room */}
                {(isHost || isAdmin) && waitingRoom.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-amber-400 text-xs font-bold mb-2 uppercase">Waiting Room ({waitingRoom.length})</h4>
                    {waitingRoom.map(w => (
                      <div key={w.user_id} className="flex items-center justify-between bg-amber-500/10 rounded-lg p-2 mb-1">
                        <span className="text-white text-sm">{w.user_name}</span>
                        <div className="flex gap-1">
                          <button onClick={() => admitUser(w.user_id)} className="bg-green-600 text-white p-1 rounded" data-testid={`admit-${w.user_id}`}><UserCheck className="w-3 h-3" /></button>
                          <button onClick={() => rejectUser(w.user_id)} className="bg-red-500 text-white p-1 rounded" data-testid={`reject-${w.user_id}`}><UserX className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <h4 className="text-slate-400 text-xs font-bold mb-2 uppercase">In Meeting ({participants.length})</h4>
                {participants.map(p => (
                  <div key={p.user_id} className="flex items-center gap-2 p-2 text-white text-sm">
                    <div className="w-6 h-6 rounded-full bg-deep-blue flex items-center justify-center text-[10px] font-bold">{p.user_name?.charAt(0)?.toUpperCase()}</div>
                    <span>{p.user_name}</span>
                    {p.user_id === meeting?.host_id && <span className="text-[10px] bg-awareness-blue/20 text-awareness-blue px-1.5 rounded">Host</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-slate-800 px-4 py-3 flex items-center justify-center gap-3 border-t border-slate-700">
        <Button size="sm" variant={audioEnabled ? "outline" : "destructive"} onClick={toggleAudio} className={audioEnabled ? "border-slate-600 text-white" : ""} data-testid="meeting-toggle-audio">
          {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </Button>
        <Button size="sm" variant={videoEnabled ? "outline" : "destructive"} onClick={toggleVideo} className={videoEnabled ? "border-slate-600 text-white" : ""} data-testid="meeting-toggle-video">
          {videoEnabled ? <Camera className="w-5 h-5" /> : <CameraOff className="w-5 h-5" />}
        </Button>
        <Button size="sm" variant="outline" className="border-slate-600 text-white" onClick={() => { setShowChat(!showChat); setShowParticipants(false); }}>
          <MessageCircle className="w-5 h-5" />
        </Button>
        <Button size="sm" variant="outline" className="border-slate-600 text-white relative" onClick={() => { setShowParticipants(!showParticipants); setShowChat(false); }}>
          <Users className="w-5 h-5" />
          {waitingRoom.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[9px] rounded-full flex items-center justify-center">{waitingRoom.length}</span>}
        </Button>
        {(isHost || isAdmin) ? (
          <Button size="sm" onClick={endMeeting} className="bg-red-500 text-white hover:bg-red-600 ml-4" data-testid="end-meeting-btn"><PhoneOff className="w-5 h-5 mr-1" /> End</Button>
        ) : (
          <Button size="sm" onClick={leaveMeeting} className="bg-red-500 text-white hover:bg-red-600 ml-4"><PhoneOff className="w-5 h-5 mr-1" /> Leave</Button>
        )}
      </div>
    </div>
  );
}

export default function ConferenceRoomPage() {
  const { meetingId } = useParams();
  if (meetingId) return <MeetingRoom meetingId={meetingId} />;
  return <MeetingList />;
}
